import {
  TEntityResponse,
  TEntityListResponse,
  TPassportizationFieldResBody,
  TPassportizationFieldSummaryResBody,
  TUpdatePassportizationFieldReqBody,
  TPassportizationTemplateResBody,
  TPassportizationTemplateSummaryResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TPassportizationClient = {
  list_fields(
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'passportization-field', TPassportizationFieldSummaryResBody> | undefined>;
  list_template_fields(
    templateId: string,
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'passportization-field', TPassportizationFieldSummaryResBody> | undefined>;
  get_field(
    fieldId: string
  ): Promise<TEntityResponse<'passportization-field', TPassportizationFieldResBody> | undefined>;
  update_field(
    id: string,
    body: TUpdatePassportizationFieldReqBody
  ): Promise<TEntityResponse<'passportization-field', TPassportizationFieldResBody> | undefined>;
  list_templates(
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'passportization-template', TPassportizationTemplateSummaryResBody> | undefined>;
  get_template(
    templateId: string
  ): Promise<TEntityResponse<'passportization-template', TPassportizationTemplateResBody> | undefined>;
};

function PassportizationClient(apiClient: TAPIClient): TPassportizationClient {
  return {
    async list_fields(
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'passportization-field', TPassportizationFieldSummaryResBody> | undefined> {
      const data = await apiClient.get<
        TEntityListResponse<'passportization-field', TPassportizationFieldSummaryResBody>
      >(`/passportization-fields?limit=${limit}&page=${page}&sort[key]=asc`);

      if (data && data.type !== 'passportization-field') {
        throw new Error('Invalid entity type received, expected: passportization-field, received: ' + data.type);
      }

      return data;
    },

    async list_template_fields(
      templateId: string,
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'passportization-field', TPassportizationFieldSummaryResBody> | undefined> {
      const data = await apiClient.get<
        TEntityListResponse<'passportization-field', TPassportizationFieldSummaryResBody>
      >(
        `/passportization-fields?limit=${limit}&page=${page}&filter[passportization_template_id]=${templateId}&sort[key]=asc`
      );

      if (data && data.type !== 'passportization-field') {
        throw new Error('Invalid entity type received, expected: passportization-field, received: ' + data.type);
      }

      return data;
    },

    async get_field(
      fieldId: string
    ): Promise<TEntityResponse<'passportization-field', TPassportizationFieldResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'passportization-field', TPassportizationFieldResBody>>(
        `/passportization-fields/${fieldId}`
      );

      if (data && data.type !== 'passportization-field') {
        throw new Error('Invalid entity type received, expected: passportization-field, received: ' + data.type);
      }

      return data;
    },

    async update_field(
      id: string,
      body: TUpdatePassportizationFieldReqBody
    ): Promise<TEntityResponse<'passportization-field', TPassportizationFieldResBody> | undefined> {
      const data = await apiClient.put<
        TUpdatePassportizationFieldReqBody,
        TEntityResponse<'passportization-field', TPassportizationFieldResBody>
      >(`/passportization-fields/${id}`, body);

      if (data && data.type !== 'passportization-field') {
        throw new Error('Invalid entity type received, expected: passportization-field, received: ' + data.type);
      }

      return data;
    },

    async list_templates(
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'passportization-template', TPassportizationTemplateSummaryResBody> | undefined> {
      const data = await apiClient.get<
        TEntityListResponse<'passportization-template', TPassportizationTemplateSummaryResBody>
      >(`/passportization-templates?limit=${limit}&page=${page}`);

      if (data && data.type !== 'passportization-template') {
        throw new Error('Invalid entity type received, expected: passportization-template, received: ' + data.type);
      }

      return data;
    },

    async get_template(
      templateId: string
    ): Promise<TEntityResponse<'passportization-template', TPassportizationTemplateResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'passportization-template', TPassportizationTemplateResBody>>(
        `/passportization-templates/${templateId}`
      );

      if (data && data.type !== 'passportization-template') {
        throw new Error('Invalid entity type received, expected: passportization-template, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TPassportizationClient, PassportizationClient };
